const ZK_DB_NAME = "zk-chat-db";
const ZK_DB_VERSION = 1;
const ZK_STORE = "kv";
const BACKUP_PBKDF2_ITERATIONS = 310000;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

function assertString(value, label) {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`${label} is required.`);
  }
}

function toBase64(bytes) {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function fromBase64(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function stableParticipantHash(participantUids = []) {
  return Array.from(new Set(participantUids.filter(Boolean))).sort().join(":");
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(ZK_DB_NAME, ZK_DB_VERSION);
    req.onerror = () => reject(req.error || new Error("IndexedDB open failed."));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(ZK_STORE)) {
        db.createObjectStore(ZK_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ZK_STORE, "readonly");
    const store = tx.objectStore(ZK_STORE);
    const req = store.get(key);
    req.onerror = () => reject(req.error || new Error("IndexedDB read failed."));
    req.onsuccess = () => resolve(req.result ?? null);
  });
}

async function idbSet(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ZK_STORE, "readwrite");
    const store = tx.objectStore(ZK_STORE);
    const req = store.put(value, key);
    req.onerror = () => reject(req.error || new Error("IndexedDB write failed."));
    req.onsuccess = () => resolve();
  });
}

async function idbEntriesWithPrefix(prefix) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ZK_STORE, "readonly");
    const store = tx.objectStore(ZK_STORE);
    const req = store.openCursor();
    const out = {};
    req.onerror = () => reject(req.error || new Error("IndexedDB cursor failed."));
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        resolve(out);
        return;
      }
      const key = String(cursor.key || "");
      if (key.startsWith(prefix)) {
        out[key] = cursor.value;
      }
      cursor.continue();
    };
  });
}

function identityPrivateKeyId(uid) {
  return `identity:private:${uid}`;
}

function identityPublicKeyId(uid) {
  return `identity:public:${uid}`;
}

function senderKeyRecordId(holderUid, chatId, senderUid) {
  return `sender-key:${holderUid}:${chatId}:${senderUid}`;
}

// ── In-memory crypto caches ───────────────────────────────────────────
// These shave the per-send cost for large groups: a 150-person send used to
// do 150 IDB reads + 150 ECDH derivations + 150 HKDF derivations on every
// message. With caches, all of that work happens once per recipient pubkey.
const IDENTITY_PRIVATE_KEY_CACHE = new Map(); // uid -> Promise<CryptoKey>
const PEER_PUBLIC_KEY_CACHE = new Map(); // base64 pub -> Promise<CryptoKey>
const WRAPPING_KEY_CACHE = new Map(); // cache key -> Promise<CryptoKey>
const WRAPPING_KEY_CACHE_MAX = 2000;

function invalidateIdentityPrivateKeyCache(uid) {
  if (uid) IDENTITY_PRIVATE_KEY_CACHE.delete(uid);
  // The wrapping-key cache embeds derived ECDH output from this private key.
  // If the identity changed, every cached wrapping key for that uid is stale.
  WRAPPING_KEY_CACHE.clear();
}

async function importPublicKey(publicKey) {
  if (!publicKey) throw new Error("Missing public key.");
  if (typeof publicKey === "string") {
    const cached = PEER_PUBLIC_KEY_CACHE.get(publicKey);
    if (cached) return cached;
    const promise = crypto.subtle.importKey(
      "spki",
      fromBase64(publicKey),
      { name: "ECDH", namedCurve: "P-256" },
      true,
      []
    );
    PEER_PUBLIC_KEY_CACHE.set(publicKey, promise);
    promise.catch(() => PEER_PUBLIC_KEY_CACHE.delete(publicKey));
    return promise;
  }
  return crypto.subtle.importKey(
    "jwk",
    publicKey,
    { name: "ECDH", namedCurve: "P-256" },
    true,
    []
  );
}

async function loadIdentityPrivateKey(uid) {
  const cached = IDENTITY_PRIVATE_KEY_CACHE.get(uid);
  if (cached) return cached;
  const promise = (async () => {
    const jwk = await idbGet(identityPrivateKeyId(uid));
    if (!jwk) {
      throw new Error("Missing local private key. Run generateKeys() and publish your public key first.");
    }
    return crypto.subtle.importKey(
      "jwk",
      jwk,
      { name: "ECDH", namedCurve: "P-256" },
      true,
      ["deriveBits"]
    );
  })();
  IDENTITY_PRIVATE_KEY_CACHE.set(uid, promise);
  promise.catch(() => IDENTITY_PRIVATE_KEY_CACHE.delete(uid));
  return promise;
}

async function derivePairwiseSecret({ myUid, peerPublicKey }) {
  const myPrivateKey = await loadIdentityPrivateKey(myUid);
  const peerKey = await importPublicKey(peerPublicKey);
  return crypto.subtle.deriveBits(
    {
      name: "ECDH",
      public: peerKey,
    },
    myPrivateKey,
    256
  );
}

async function deriveAesKeyFromSecret(rawSecret, { saltLabel, infoLabel, usages }) {
  const ikm = await crypto.subtle.importKey("raw", rawSecret, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: textEncoder.encode(saltLabel),
      info: textEncoder.encode(infoLabel),
    },
    ikm,
    {
      name: "AES-GCM",
      length: 256,
    },
    false,
    usages
  );
}

async function deriveDmAesKey({ chatId, myUid, otherUid, otherPublicKey, usages }) {
  // AES-GCM CryptoKeys are reusable for both encrypt+decrypt; cache by the full
  // input tuple so repeated sends in the same group reuse the same wrapping key
  // instead of redoing ECDH+HKDF for every recipient on every message.
  const usageKey = Array.isArray(usages) ? [...usages].sort().join(",") : String(usages || "");
  const cacheKey = `${chatId}|${myUid}|${otherUid}|${typeof otherPublicKey === "string" ? otherPublicKey : ""}|${usageKey}`;
  if (typeof otherPublicKey === "string" && otherPublicKey) {
    const cached = WRAPPING_KEY_CACHE.get(cacheKey);
    if (cached) return cached;
  }
  const promise = (async () => {
    const pairwiseSecret = await derivePairwiseSecret({ myUid, peerPublicKey: otherPublicKey });
    const pairLabel = [myUid, otherUid].sort().join(":");
    return deriveAesKeyFromSecret(pairwiseSecret, {
      saltLabel: `dm:${chatId}:${pairLabel}`,
      infoLabel: "zk-dm-message",
      usages,
    });
  })();
  if (typeof otherPublicKey === "string" && otherPublicKey) {
    if (WRAPPING_KEY_CACHE.size >= WRAPPING_KEY_CACHE_MAX) {
      // Simple FIFO eviction to keep this bounded across many conversations.
      const firstKey = WRAPPING_KEY_CACHE.keys().next().value;
      if (firstKey !== undefined) WRAPPING_KEY_CACHE.delete(firstKey);
    }
    WRAPPING_KEY_CACHE.set(cacheKey, promise);
    promise.catch(() => WRAPPING_KEY_CACHE.delete(cacheKey));
  }
  return promise;
}

async function importAesKey(rawBytes, usages) {
  return crypto.subtle.importKey("raw", rawBytes, { name: "AES-GCM" }, false, usages);
}

async function aesEncryptString({ key, plaintext, aadLabel }) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: textEncoder.encode(aadLabel),
      tagLength: 128,
    },
    key,
    textEncoder.encode(plaintext)
  );
  return {
    iv: toBase64(iv),
    ciphertext: toBase64(new Uint8Array(encrypted)),
  };
}

async function aesDecryptString({ key, ivBase64, ciphertextBase64, aadLabel }) {
  const iv = fromBase64(ivBase64);
  const ciphertext = fromBase64(ciphertextBase64);
  const plaintext = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: textEncoder.encode(aadLabel),
      tagLength: 128,
    },
    key,
    ciphertext
  );
  return textDecoder.decode(plaintext);
}

async function loadSenderKeyRecord(holderUid, chatId, senderUid) {
  const record = await idbGet(senderKeyRecordId(holderUid, chatId, senderUid));
  if (!record || typeof record !== "object" || typeof record.raw !== "string") return null;
  return {
    raw: record.raw,
    version: Number(record.version || 1),
    participantsHash: typeof record.participantsHash === "string" ? record.participantsHash : "",
    updatedAt: Number(record.updatedAt || Date.now()),
  };
}

async function saveSenderKeyRecord(holderUid, chatId, senderUid, record) {
  await idbSet(senderKeyRecordId(holderUid, chatId, senderUid), {
    raw: record.raw,
    version: Number(record.version || 1),
    participantsHash: record.participantsHash || "",
    updatedAt: Number(record.updatedAt || Date.now()),
  });
}

async function getOrCreateSenderKeyForChat({ holderUid, chatId, senderUid, participantUids = [] }) {
  const hash = stableParticipantHash(participantUids);
  const current = await loadSenderKeyRecord(holderUid, chatId, senderUid);
  if (!current) {
    const raw = crypto.getRandomValues(new Uint8Array(32));
    const record = {
      raw: toBase64(raw),
      version: 1,
      participantsHash: hash,
      updatedAt: Date.now(),
    };
    await saveSenderKeyRecord(holderUid, chatId, senderUid, record);
    return { ...record, rawBytes: raw, rotated: true };
  }

  if (holderUid === senderUid && current.participantsHash !== hash) {
    const raw = crypto.getRandomValues(new Uint8Array(32));
    const next = {
      raw: toBase64(raw),
      version: Number(current.version || 1) + 1,
      participantsHash: hash,
      updatedAt: Date.now(),
    };
    await saveSenderKeyRecord(holderUid, chatId, senderUid, next);
    return { ...next, rawBytes: raw, rotated: true };
  }

  return {
    ...current,
    rawBytes: fromBase64(current.raw),
    rotated: false,
  };
}

async function wrapSenderKeyForRecipient({
  chatId,
  senderUid,
  recipientUid,
  senderKeyRaw,
  senderKeyVersion,
  recipientPublicKey,
}) {
  const wrappingKey = await deriveDmAesKey({
    chatId,
    myUid: senderUid,
    otherUid: recipientUid,
    otherPublicKey: recipientPublicKey,
    usages: ["encrypt", "decrypt"],
  });
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: textEncoder.encode(`sender-key:${chatId}:${senderUid}:${recipientUid}:${senderKeyVersion}`),
      tagLength: 128,
    },
    wrappingKey,
    senderKeyRaw
  );
  return {
    iv: toBase64(iv),
    ciphertext: toBase64(new Uint8Array(encrypted)),
  };
}

async function unwrapSenderKeyFromPacket({
  chatId,
  senderUid,
  myUid,
  senderPublicKey,
  senderKeyVersion,
  packet,
}) {
  const wrappingKey = await deriveDmAesKey({
    chatId,
    myUid,
    otherUid: senderUid,
    otherPublicKey: senderPublicKey,
    usages: ["encrypt", "decrypt"],
  });
  const decrypted = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: fromBase64(packet.iv),
      additionalData: textEncoder.encode(`sender-key:${chatId}:${senderUid}:${myUid}:${senderKeyVersion}`),
      tagLength: 128,
    },
    wrappingKey,
    fromBase64(packet.ciphertext)
  );
  return new Uint8Array(decrypted);
}

function resolveKind(kind, payload) {
  return kind || payload?.kind || "dm";
}

function senderKeyBoxesFromPayload(payload = {}) {
  if (!payload.senderKeyBoxes || typeof payload.senderKeyBoxes !== "object") return {};
  const out = {};
  Object.entries(payload.senderKeyBoxes).forEach(([uid, packet]) => {
    if (!uid || typeof packet !== "object") return;
    if (typeof packet.iv !== "string" || typeof packet.ciphertext !== "string") return;
    out[uid] = { iv: packet.iv, ciphertext: packet.ciphertext };
  });
  return out;
}

async function resolveSenderPublicKeyForUnwrap({
  payload,
  actualSenderUid,
  myUid,
  publicKeysByUid = {},
}) {
  let senderPublicKey = (typeof payload.senderPub === "string" && payload.senderPub)
    ? payload.senderPub
    : publicKeysByUid[actualSenderUid];
  if (!senderPublicKey && actualSenderUid === myUid) {
    try {
      const identity = await ensureIdentityKeyPair({ uid: myUid });
      senderPublicKey = identity.publicKeyBase64 || "";
    } catch {
      senderPublicKey = "";
    }
  }
  return senderPublicKey || "";
}

async function refreshSenderKeyRecordFromPayload({
  chatId,
  payload,
  myUid,
  actualSenderUid,
  senderKeyVersion,
  publicKeysByUid = {},
  existingRecord = null,
}) {
  const boxes = senderKeyBoxesFromPayload(payload);
  const packet = boxes[myUid];
  if (!packet) {
    throw new Error("Missing sender key packet for this recipient/version.");
  }

  const senderPublicKey = await resolveSenderPublicKeyForUnwrap({
    payload,
    actualSenderUid,
    myUid,
    publicKeysByUid,
  });
  if (!senderPublicKey) {
    throw new Error("Missing sender public key for sender-key unwrap.");
  }

  const rawBytes = await unwrapSenderKeyFromPacket({
    chatId,
    senderUid: actualSenderUid,
    myUid,
    senderPublicKey,
    senderKeyVersion,
    packet,
  });
  const nextRecord = {
    raw: toBase64(rawBytes),
    version: senderKeyVersion,
    participantsHash: existingRecord?.participantsHash || "",
    updatedAt: Date.now(),
  };
  await saveSenderKeyRecord(myUid, chatId, actualSenderUid, nextRecord);
  return nextRecord;
}

async function deriveBackupAesKey(passphrase, saltBytes, usages) {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    textEncoder.encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: saltBytes,
      iterations: BACKUP_PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    baseKey,
    {
      name: "AES-GCM",
      length: 256,
    },
    false,
    usages
  );
}

export async function generateKeys({ uid }) {
  assertString(uid, "uid");

  const keyPair = await crypto.subtle.generateKey(
    {
      name: "ECDH",
      namedCurve: "P-256",
    },
    true,
    ["deriveBits"]
  );

  const privateJwk = await crypto.subtle.exportKey("jwk", keyPair.privateKey);
  const publicJwk = await crypto.subtle.exportKey("jwk", keyPair.publicKey);
  const publicSpki = await crypto.subtle.exportKey("spki", keyPair.publicKey);
  const publicKeyBase64 = toBase64(new Uint8Array(publicSpki));

  await idbSet(identityPrivateKeyId(uid), privateJwk);
  await idbSet(identityPublicKeyId(uid), publicJwk);
  invalidateIdentityPrivateKeyCache(uid);

  return {
    uid,
    algorithm: "ECDH-P256",
    publicKeyBase64,
    publicKeyJwk: publicJwk,
  };
}

export async function ensureIdentityKeyPair({ uid, existingPublicKeyBase64 = "" }) {
  assertString(uid, "uid");
  const privateJwk = await idbGet(identityPrivateKeyId(uid));
  const publicJwk = await idbGet(identityPublicKeyId(uid));
  if (!privateJwk || !publicJwk) {
    const generated = await generateKeys({ uid });
    return { ...generated, created: true, needsProfilePublish: true };
  }

  const publicKey = await crypto.subtle.importKey(
    "jwk",
    publicJwk,
    { name: "ECDH", namedCurve: "P-256" },
    true,
    []
  );
  const publicSpki = await crypto.subtle.exportKey("spki", publicKey);
  const publicKeyBase64 = toBase64(new Uint8Array(publicSpki));
  return {
    uid,
    algorithm: "ECDH-P256",
    publicKeyBase64,
    publicKeyJwk: publicJwk,
    created: false,
    needsProfilePublish: publicKeyBase64 !== String(existingPublicKeyBase64 || ""),
  };
}

export async function encryptMessage({
  chatId,
  text,
  senderUid,
  kind = "dm",
  recipientUid,
  participantUids = [],
  publicKeysByUid = {},
}) {
  assertString(chatId, "chatId");
  assertString(senderUid, "senderUid");
  if (typeof text !== "string") throw new Error("text must be a string.");
  const uniqueParticipantUids = Array.from(new Set((participantUids || []).filter(Boolean)));
  const seedSenderPublic = typeof publicKeysByUid[senderUid] === "string"
    ? publicKeysByUid[senderUid]
    : "";
  let senderPublicKeySnapshot = "";
  try {
    const identity = await ensureIdentityKeyPair({
      uid: senderUid,
      existingPublicKeyBase64: seedSenderPublic,
    });
    senderPublicKeySnapshot = identity.publicKeyBase64 || "";
  } catch {
    senderPublicKeySnapshot = seedSenderPublic;
  }

  const messageKind = resolveKind(kind, null);

  if (messageKind === "dm") {
    assertString(recipientUid, "recipientUid");
    const recipientPublicKey = publicKeysByUid[recipientUid];
    if (!recipientPublicKey) {
      throw new Error("Missing recipient public key for DM encryption.");
    }

    const dmKey = await deriveDmAesKey({
      chatId,
      myUid: senderUid,
      otherUid: recipientUid,
      otherPublicKey: recipientPublicKey,
      usages: ["encrypt", "decrypt"],
    });

    const encrypted = await aesEncryptString({
      key: dmKey,
      plaintext: text,
      aadLabel: `dm:${chatId}:${senderUid}`,
    });

    return {
      v: 1,
      kind: "dm",
      alg: "AES-GCM",
      senderUid,
      iv: encrypted.iv,
      ciphertext: encrypted.ciphertext,
      ...(senderPublicKeySnapshot ? { senderPub: senderPublicKeySnapshot } : {}),
      recipientPub: recipientPublicKey,
    };
  }

  if (messageKind === "group") {
    const senderKeyState = await getOrCreateSenderKeyForChat({
      holderUid: senderUid,
      chatId,
      senderUid,
      participantUids: uniqueParticipantUids,
    });
    const senderKey = await importAesKey(senderKeyState.rawBytes, ["encrypt", "decrypt"]);

    const encrypted = await aesEncryptString({
      key: senderKey,
      plaintext: text,
      aadLabel: `group:${chatId}:${senderUid}:${senderKeyState.version}`,
    });

    // Include sender-key packets on every group message so recipients can recover
    // after browser data loss/new device without waiting for a membership rotation.
    // Wrap recipients in parallel — for a 150-person group the sequential await
    // version was dominating send latency.
    const wrapTargets = uniqueParticipantUids
      .map((uid) => {
        const recipientPublicKey = uid === senderUid
          ? senderPublicKeySnapshot
          : publicKeysByUid[uid];
        if (!recipientPublicKey) return null;
        return { uid, recipientPublicKey };
      })
      .filter(Boolean);
    const wrapped = await Promise.all(
      wrapTargets.map(({ uid, recipientPublicKey }) =>
        wrapSenderKeyForRecipient({
          chatId,
          senderUid,
          recipientUid: uid,
          senderKeyRaw: senderKeyState.rawBytes,
          senderKeyVersion: senderKeyState.version,
          recipientPublicKey,
        })
      )
    );
    const senderKeyBoxes = {};
    for (let i = 0; i < wrapTargets.length; i += 1) {
      senderKeyBoxes[wrapTargets[i].uid] = wrapped[i];
    }

    return {
      v: 1,
      kind: "group",
      alg: "AES-GCM",
      senderUid,
      iv: encrypted.iv,
      ciphertext: encrypted.ciphertext,
      senderKeyVersion: senderKeyState.version,
      senderKeyBoxes,
      ...(senderPublicKeySnapshot ? { senderPub: senderPublicKeySnapshot } : {}),
    };
  }

  throw new Error(`Unsupported message kind: ${messageKind}`);
}

export async function warmGroupMessageEncryption({
  chatId,
  senderUid,
  participantUids = [],
  publicKeysByUid = {},
}) {
  assertString(chatId, "chatId");
  assertString(senderUid, "senderUid");

  const uniqueParticipantUids = Array.from(new Set((participantUids || []).filter(Boolean)));
  if (uniqueParticipantUids.length < 3) {
    return { warmed: 0 };
  }

  const seedSenderPublic = typeof publicKeysByUid[senderUid] === "string"
    ? publicKeysByUid[senderUid]
    : "";
  let senderPublicKeySnapshot = seedSenderPublic;
  try {
    const identity = await ensureIdentityKeyPair({
      uid: senderUid,
      existingPublicKeyBase64: seedSenderPublic,
    });
    senderPublicKeySnapshot = identity.publicKeyBase64 || seedSenderPublic;
  } catch {
    senderPublicKeySnapshot = seedSenderPublic;
  }

  await getOrCreateSenderKeyForChat({
    holderUid: senderUid,
    chatId,
    senderUid,
    participantUids: uniqueParticipantUids,
  });

  const wrapTargets = uniqueParticipantUids
    .map((uid) => {
      const recipientPublicKey = uid === senderUid
        ? senderPublicKeySnapshot
        : publicKeysByUid[uid];
      if (!recipientPublicKey) return null;
      return { uid, recipientPublicKey };
    })
    .filter(Boolean);

  await Promise.all(
    wrapTargets.map(({ uid, recipientPublicKey }) =>
      deriveDmAesKey({
        chatId,
        myUid: senderUid,
        otherUid: uid,
        otherPublicKey: recipientPublicKey,
        usages: ["encrypt", "decrypt"],
      })
    )
  );

  return { warmed: wrapTargets.length };
}

export async function decryptMessage({
  chatId,
  payload,
  myUid,
  kind,
  senderUid,
  otherUid,
  publicKeysByUid = {},
}) {
  assertString(chatId, "chatId");
  assertString(myUid, "myUid");
  if (!payload || typeof payload !== "object") {
    throw new Error("payload is required.");
  }

  const messageKind = resolveKind(kind, payload);
  const actualSenderUid = senderUid || payload.senderUid;

  if (messageKind === "dm") {
    const peerUid = actualSenderUid === myUid ? otherUid : actualSenderUid;
    assertString(peerUid, "peerUid");

    const peerPublicKey = actualSenderUid === myUid
      ? (typeof payload.recipientPub === "string" && payload.recipientPub
        ? payload.recipientPub
        : publicKeysByUid[peerUid])
      : (typeof payload.senderPub === "string" && payload.senderPub
        ? payload.senderPub
        : publicKeysByUid[peerUid]);
    if (!peerPublicKey) {
      throw new Error("Missing peer public key for DM decryption.");
    }

    const dmKey = await deriveDmAesKey({
      chatId,
      myUid,
      otherUid: peerUid,
      otherPublicKey: peerPublicKey,
      usages: ["encrypt", "decrypt"],
    });

    return aesDecryptString({
      key: dmKey,
      ivBase64: payload.iv,
      ciphertextBase64: payload.ciphertext,
      aadLabel: `dm:${chatId}:${actualSenderUid}`,
    });
  }

  if (messageKind === "group") {
    assertString(actualSenderUid, "senderUid");
    const senderKeyVersion = Number(payload.senderKeyVersion || 1);

    let senderKeyRecord = await loadSenderKeyRecord(myUid, chatId, actualSenderUid);
    const needsRefresh = !senderKeyRecord || Number(senderKeyRecord.version || 1) !== senderKeyVersion;

    if (needsRefresh) {
      senderKeyRecord = await refreshSenderKeyRecordFromPayload({
        chatId,
        payload,
        myUid,
        actualSenderUid,
        senderKeyVersion,
        publicKeysByUid,
        existingRecord: senderKeyRecord,
      });
    }

    const decryptWithRecord = async (record) => {
      const senderKey = await importAesKey(fromBase64(record.raw), ["encrypt", "decrypt"]);
      return aesDecryptString({
        key: senderKey,
        ivBase64: payload.iv,
        ciphertextBase64: payload.ciphertext,
        aadLabel: `group:${chatId}:${actualSenderUid}:${senderKeyVersion}`,
      });
    };

    try {
      return await decryptWithRecord(senderKeyRecord);
    } catch (decryptErr) {
      const hasPacket = !!senderKeyBoxesFromPayload(payload)[myUid];
      if (!hasPacket) {
        throw decryptErr;
      }
      try {
        senderKeyRecord = await refreshSenderKeyRecordFromPayload({
          chatId,
          payload,
          myUid,
          actualSenderUid,
          senderKeyVersion,
          publicKeysByUid,
          existingRecord: senderKeyRecord,
        });
        return await decryptWithRecord(senderKeyRecord);
      } catch {
        throw decryptErr;
      }
    }
  }

  throw new Error(`Unsupported message kind: ${messageKind}`);
}

export async function backupLocalKeys({ uid, passphrase, includeSenderKeys = true }) {
  assertString(uid, "uid");
  assertString(passphrase, "passphrase");

  const privateJwk = await idbGet(identityPrivateKeyId(uid));
  const publicJwk = await idbGet(identityPublicKeyId(uid));
  if (!privateJwk || !publicJwk) {
    throw new Error("No local identity keys found to backup.");
  }

  const senderKeys = includeSenderKeys
    ? await idbEntriesWithPrefix(`sender-key:${uid}:`)
    : {};
  const snapshot = {
    v: 1,
    uid,
    privateJwk,
    publicJwk,
    senderKeys,
    exportedAt: Date.now(),
  };

  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const backupKey = await deriveBackupAesKey(passphrase, salt, ["encrypt", "decrypt"]);
  const encrypted = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: textEncoder.encode(`zk-backup:${uid}:v1`),
      tagLength: 128,
    },
    backupKey,
    textEncoder.encode(JSON.stringify(snapshot))
  );

  return {
    v: 1,
    uid,
    alg: "AES-GCM",
    kdf: "PBKDF2",
    hash: "SHA-256",
    iterations: BACKUP_PBKDF2_ITERATIONS,
    salt: toBase64(salt),
    iv: toBase64(iv),
    ciphertext: toBase64(new Uint8Array(encrypted)),
    createdAt: Date.now(),
  };
}

export async function recoverLocalKeys({ uid, passphrase, backupPayload }) {
  assertString(uid, "uid");
  assertString(passphrase, "passphrase");
  if (!backupPayload || typeof backupPayload !== "object") {
    throw new Error("backupPayload is required.");
  }

  const salt = fromBase64(String(backupPayload.salt || ""));
  const iv = fromBase64(String(backupPayload.iv || ""));
  const ciphertext = fromBase64(String(backupPayload.ciphertext || ""));
  const backupKey = await deriveBackupAesKey(passphrase, salt, ["encrypt", "decrypt"]);

  let decrypted;
  try {
    decrypted = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv,
        additionalData: textEncoder.encode(`zk-backup:${uid}:v1`),
        tagLength: 128,
      },
      backupKey,
      ciphertext
    );
  } catch {
    throw new Error("Recovery failed. Wrong passphrase or corrupt backup.");
  }

  const parsed = JSON.parse(textDecoder.decode(decrypted));
  if (!parsed || parsed.uid !== uid) {
    throw new Error("Backup uid does not match the current user.");
  }

  await idbSet(identityPrivateKeyId(uid), parsed.privateJwk);
  await idbSet(identityPublicKeyId(uid), parsed.publicJwk);
  invalidateIdentityPrivateKeyCache(uid);

  const senderEntries = parsed.senderKeys && typeof parsed.senderKeys === "object" ? parsed.senderKeys : {};
  const keys = Object.keys(senderEntries);
  for (let i = 0; i < keys.length; i += 1) {
    await idbSet(keys[i], senderEntries[keys[i]]);
  }

  return {
    restoredSenderKeyCount: keys.length,
    restoredAt: Date.now(),
  };
}
